import { API_URL } from '../config';
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

const api = axios.create({ baseURL: API_URL });

interface Base {
  id: number;
  nomeBase: string;
  totalContatos: number;
  createdAt: string;
  ativo: boolean;
}

const inputCls = 'w-full bg-gray-50 border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300 transition-all';

export default function BasesPage() {
  const [bases, setBases] = useState<Base[]>([]);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [nomeBase, setNomeBase] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editBase, setEditBase] = useState<Base | null>(null);
  const [editNome, setEditNome] = useState('');
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [warn, setWarn] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadBases = useCallback(async () => {
    try {
      const { data } = await api.get<Base[]>('/bases');
      setBases(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadBases();
  }, [loadBases]);

  const showToast = (msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const showWarn = (msg: string) => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    setWarn(msg);
    warnTimer.current = setTimeout(() => setWarn(null), 3000);
  };

  const handleImport = async () => {
    if (!nomeBase.trim()) {
      setError('Informe o nome da lista');
      return;
    }
    if (bases.some((b) => b.nomeBase.toLowerCase() === nomeBase.trim().toLowerCase())) {
      showWarn('Ja existe uma lista com esse nome');
      return;
    }
    if (!file) {
      setError('Selecione um arquivo .xlsx ou .csv');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('nome_base', nomeBase.trim());
      formData.append('file', file);
      await api.post('/bases', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setModalOpen(false);
      setNomeBase('');
      setFile(null);
      await loadBases();
      showToast('Lista importada com sucesso');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erro ao importar lista');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (base: Base) => {
    setEditBase(base);
    setEditNome(base.nomeBase);
    setEditError('');
  };

  const handleEdit = async () => {
    if (!editBase) return;
    if (!editNome.trim()) {
      setEditError('Informe o nome da lista');
      return;
    }
    if (bases.some((b) => b.id !== editBase.id && b.nomeBase.toLowerCase() === editNome.trim().toLowerCase())) {
      showWarn('Ja existe uma lista com esse nome');
      return;
    }

    setEditLoading(true);
    setEditError('');
    try {
      await api.patch(`/bases/${editBase.id}`, { nome_base: editNome.trim() });
      setEditBase(null);
      await loadBases();
      showToast('Lista atualizada com sucesso');
    } catch (e: any) {
      setEditError(e?.response?.data?.message || 'Erro ao atualizar lista');
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleAtivo = async (base: Base) => {
    try {
      await api.patch(`/bases/${base.id}/toggle`);
      await loadBases();
    } catch {
      showToast('Erro ao alterar status da lista');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/bases/${id}`);
      await loadBases();
      showToast('Lista excluida com sucesso');
    } catch {
      showToast('Erro ao excluir lista');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const openModal = () => {
    setNomeBase('');
    setFile(null);
    setError('');
    setModalOpen(true);
  };

  const filtered = bases.filter((b) => !search.trim() || b.nomeBase.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={openModal}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg transition-all hover:bg-gray-800 flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          Importar Lista
        </button>
        <div className="flex items-center gap-2 border rounded-lg px-3 py-2 w-64 bg-white border border-gray-200">
          <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar lista..."
            className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400">Nome da Lista</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-400">Numeros</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-400">Data</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-400">Status</th>
              <th className="px-4 py-2.5 w-24 text-xs font-medium text-gray-400">Acoes</th>
            </tr>
          </thead>
          <tbody className=" bg-white border border-gray-200">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-20 text-center text-gray-400 text-sm">
                  {bases.length === 0 ? 'Nenhuma lista importada' : 'Nenhuma lista encontrada'}
                </td>
              </tr>
            ) : (
              filtered.map((base, i) => (
                <tr key={base.id} className={`hover:bg-gray-50 transition-colors ${i !== filtered.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  <td className="px-4 py-3 text-sm text-gray-900">{base.nomeBase}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 font-mono text-center">{base.totalContatos.toLocaleString('pt-BR')}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-center">{new Date(base.createdAt).toLocaleDateString('pt-BR')}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${base.ativo ? ' border-emerald-200' : ' border-red-200'} bg-emerald-50 text-emerald-700`}>
                      {base.ativo ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleToggleAtivo(base)}
                        title={base.ativo ? 'Desativar lista' : 'Ativar lista'}
                        className={`relative inline-flex items-center w-8 h-4 rounded-full transition-colors duration-200 focus:outline-none ${base.ativo ? 'bg-emerald-500' : 'bg-red-400'}`}
                      >
                        <span className={`inline-block w-2.5 h-2.5 rounded-full shadow transition-transform duration-200 ${base.ativo ? 'translate-x-4' : 'translate-x-0.5'} bg-white border border-gray-200`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(base)}
                        className="w-7 h-7 flex items-center justify-center rounded text-amber-500 hover:text-yellow-700 hover:bg-yellow-50 transition-all"
                        title="Editar lista"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmId(base.id)}
                        className="w-7 h-7 flex items-center justify-center rounded text-red-500 hover:text-red-500 hover:bg-red-50 transition-all"
                        title="Excluir lista"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-xl w-full max-w-sm flex flex-col">
            <div className="relative px-6 py-5 border-b border-gray-100 text-center">
              <h3 className="text-base font-bold text-gray-900">Importar Lista de Numeros</h3>
              <button
                type="button"
                onClick={() => { setModalOpen(false); setError(''); }}
                title="Fechar"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-3">
              <input
                type="text"
                value={nomeBase}
                onChange={(e) => setNomeBase(e.target.value)}
                placeholder="Nome da lista"
                className={inputCls}
              />

              <div
                onClick={() => fileInputRef.current?.click()}
                onMouseDown={(e) => e.preventDefault()}
                className="w-full border border-dashed border-gray-300 rounded-md px-4 py-5 flex flex-col items-center gap-2 cursor-pointer hover:border-gray-500 hover: transition-all bg-white border border-gray-200"
              >
                {file ? (
                  <span className="text-xs text-emerald-700 font-medium">{file.name}</span>
                ) : (
                  <>
                    <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-xs text-gray-500">Clique para selecionar o arquivo .xlsx ou .csv</span>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                aria-label="Selecionar arquivo .xlsx ou .csv"
                className="hidden"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setError(''); }}
              />

              {error && <p className="text-xs text-red-500 text-center">{error}</p>}

              <p className="text-[11px] text-gray-500 text-center">
                O arquivo deve ter apenas uma coluna de telefone: "Telefone", "TELEFONE" ou "telefone"
              </p>
            </div>

            <div className="flex gap-3 px-6 py-5 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setModalOpen(false); setError(''); }}
                className="flex-1 py-3 hover:bg-gray-100 text-gray-600 rounded-md text-sm font-medium transition-all border"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={loading || !nomeBase.trim() || !file}
                className="flex-1 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-sm font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {loading ? 'Importando...' : 'Importar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {editBase && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-xl w-full max-w-sm flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">Editar Lista</h3>
                <p className="text-xs text-gray-600 mt-0.5">Altere apenas o nome da lista</p>
              </div>
              <button
                type="button"
                onClick={() => { setEditBase(null); setEditError(''); }}
                title="Fechar"
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-3">
              <input
                type="text"
                value={editNome}
                onChange={(e) => setEditNome(e.target.value)}
                placeholder="Nome da lista"
                className={inputCls}
              />

              {editError && <p className="text-xs text-red-500 text-center">{editError}</p>}
            </div>

            <div className="flex gap-3 px-6 py-5 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setEditBase(null); setEditError(''); }}
                className="flex-1 py-3 hover:bg-gray-100 text-gray-600 rounded-md text-sm font-medium transition-all border"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleEdit}
                disabled={editLoading || !editNome.trim()}
                className="flex-1 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-sm font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {editLoading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {deleteConfirmId && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-lg shadow-xl w-full max-w-sm bg-white border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-100 text-center">
              <h3 className="text-base font-bold text-gray-900">Excluir Lista</h3>
              <p className="text-sm text-gray-500 mt-2">
                Tem certeza que deseja excluir a lista "{bases.find((b) => b.id === deleteConfirmId)?.nomeBase}"?
              </p>
            </div>
            <div className="flex gap-3 px-6 py-5">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-3 hover:bg-gray-100 text-gray-600 rounded-md text-sm font-medium transition-all border"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirmId)}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-md text-sm font-semibold transition-all"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {toast && createPortal(
        <div className="fixed right-5 top-5 z-[99999] rounded-lg bg-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-xl">
          {toast}
        </div>,
        document.body
      )}

      {warn && createPortal(
        <div className="fixed right-5 top-20 z-[99999] rounded-lg bg-amber-500 px-4 py-3 text-sm font-semibold text-white shadow-xl">
          {warn}
        </div>,
        document.body
      )}
    </div>
  );
}
